import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	FormattedText,
	IconValue,
	SvgIconName,
	Typography
} from "client/Components";
import { isNotNilOrEmpty, setTrackingId } from "client/Misc/utils";
import { reactionIconMap } from "client/Features/Reactions/ReactionsCountButton/ReactionsCountButton";
import { InlineIcon } from "./InlineIcon/InlineIcon";
import { KnowledgeItemCommentReactionNotificationDataViewModel } from "client/Api/Notifications/notifications";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import { preparePostTitleAndComment } from "./CommunityPostActivity";
import { getComputedDisplayName } from "client/Features/AddressBook/common";

export type KnowledgeItemCommentReactionActivityProps = {
	data: KnowledgeItemCommentReactionNotificationDataViewModel;
};

export const KnowledgeItemCommentReactionActivity = (props: KnowledgeItemCommentReactionActivityProps): ReactElement => {
	const { data } = props;

	const {
		commentPreviewText,
		firstname,
		lastname,
		reactionKind,
		otherReactionCount,
		isReply
	} = data;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const {
		computedDisplayName
	} = getComputedDisplayName({
		firstName: firstname,
		lastName: lastname
	});

	const iconValue: IconValue = reactionIconMap[reactionKind] !== undefined
		? {
			kind: "svg",
			name: reactionIconMap[reactionKind] as SvgIconName
		} : {
			kind: "font",
			name: "thumbs-up"
		};

	const preparedPostTitleAndComment: string = preparePostTitleAndComment(commentPreviewText ?? "");

	const titleKey = useMemo(
		(): LocalizationKey => {
			const isMultiple: boolean = otherReactionCount > 0;
			const hasPreviewText: boolean = isNotNilOrEmpty(preparedPostTitleAndComment);

			if (isMultiple) {
				return isReply
					? hasPreviewText
						? "_MultipleXReactedOnReplyY"
						: "_MultipleXReactedOnYourReply"
					: hasPreviewText
						? "_MultipleXReactedOnCommentY"
						: "_MultipleXReactedOnYourComment";
			}

			return isReply
				? hasPreviewText
					? "_XReactedOnReplyY"
					: "_XReactedOnYourReply"
				: hasPreviewText
					? "_XReactedOnCommentY"
					: "_XReactedOnYourComment";
		},
		[
			preparedPostTitleAndComment,
			isReply,
			otherReactionCount
		]
	);
	return (
		<span {...setTrackingId("title")}>
			<InlineIcon icon={iconValue}/>
			<FormattedText
				text={localize(titleKey)}
				thingsToReplace={[
					{
						what: "{{Name}}",
						filler: (
							<Typography
								weight="bold"
								trackingId="user-name"
							>
								{computedDisplayName}
							</Typography>
						)
					},
					{
						what: "{{Comment}}",
						filler: (
							<Typography
								weight="bold"
								trackingId="comment"
							>
								{preparedPostTitleAndComment}
							</Typography>
						)
					},
					{
						what: "{{NumberOfUsers}}",
						filler: (
							<Typography
								weight="bold"
								trackingId="number-of-users"
							>
								+{statisticsFormatter(otherReactionCount)}
							</Typography>
						)
					}
				]}
			/>
		</span>
	);
};

KnowledgeItemCommentReactionActivity.displayName = "KnowledgeItemCommentReactionActivity";
